import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiHome, FiMapPin, FiPhone, FiMail, FiSettings, FiUsers } from "react-icons/fi";
import { FaStore } from "react-icons/fa";
import axios from "axios";
import PageCreateHeader from "../../components/PageCreateHeader";

const EditOutletForm = () => {
  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    kota: "",
    phone: "",
    email: "",
    status: "active",
    manager_id: "",
    manager: null,
    address: "",
  });

  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
    if (role !== "owner") {
      setShowAccessDenied(true);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) {
          setError("Token tidak ditemukan. Silakan login ulang.");
          setLoading(false);
          return;
        }

        // 1. Ambil data outlet
        const outletRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/outlet/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (outletRes.data.success) {
          const outletData = outletRes.data.data;
          setFormData({
            ...outletData,
            manager_id: outletData.manager_id?.toString() || "",
            manager: outletData.manager || null,
          });

          // 2. Ambil semua pegawai dari outlet tanpa filter role
          const employeeRes = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/v1/employee?id_outlet=${id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );

          const employeeList = Array.isArray(employeeRes.data.data)
            ? employeeRes.data.data
            : [];

          setManagers(employeeList);
        }
      } catch (error) {
        console.error("Gagal mengambil data:", error);
        setError("Terjadi kesalahan saat memuat data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === "manager_id" ? Number(value) : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) {
        alert("Token tidak ditemukan. Silakan login ulang.");
        return;
      }

      const formattedData = {
        ...formData,
        manager_id: formData.manager_id ? Number(formData.manager_id) : null,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/v1/outlet/${id}`,
        formattedData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate("/enterprise/outlet");
        }, 2000);
      } else {
        alert("Gagal memperbarui outlet. Silakan coba lagi.");
      }
    } catch (error) {
      console.error("Error saat mengupdate outlet:", error);
      if (error.response) {
        alert(`Gagal update: ${error.response.data.message || "Kesalahan tidak diketahui"}`);
      } else {
        alert("Terjadi kesalahan saat mengupdate outlet.");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col space-y-6 p-6 bg-gray-50 w-full min-w-0">
        <PageCreateHeader
          icon={FaStore}
          title="Edit Outlet"
          description="Memuat data outlet..."
          breadcrumbs={[
            { label: "Enterprise", to: "/" },
            { label: "Outlet", to: "/enterprise/outlet" },
            { label: "Edit Outlet" },
          ]}
        />
        <p className="text-gray-600">Memuat data outlet...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col space-y-6 p-6 bg-gray-50 w-full min-w-0">
        <PageCreateHeader
          icon={FaStore}
          title="Edit Outlet"
          description="Terjadi kesalahan saat memuat data"
          breadcrumbs={[
            { label: "Enterprise", to: "/" },
            { label: "Outlet", to: "/enterprise/outlet" },
            { label: "Edit Outlet" },
          ]}
        />
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col space-y-6 p-6 bg-gray-50 w-full min-w-0">
      <PageCreateHeader
        icon={FaStore}
        title="Edit Outlet"
        description="Perbarui data outlet dan manager"
        breadcrumbs={[
          { label: "Enterprise", to: "/" },
          { label: "Outlet", to: "/enterprise/outlet" },
          { label: "Edit Outlet" },
        ]}
      />

      <div className="bg-white p-8 rounded-3xl shadow-xl w-full border border-gray-100">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-6">
            {/* Nama Outlet */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">Nama Outlet</label>
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                <FiHome className="text-gray-500 mr-3" />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700"
                  required
                />
              </div>
            </div>

            {/* Kota */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">Kota</label>
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                <FiMapPin className="text-gray-500 mr-3" />
                <input
                  type="text"
                  name="kota"
                  value={formData.kota}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700"
                  required
                />
              </div>
            </div>

            {/* Telepon */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">Telepon</label>
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                <FiPhone className="text-gray-500 mr-3" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700"
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">Email</label>
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                <FiMail className="text-gray-500 mr-3" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700"
                  required
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">Status</label>
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                <FiSettings className="text-gray-500 mr-3" />
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700"
                  required
                >
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>

            {/* Pilih Manager */}
            <div>
              <label className="block font-medium text-gray-700 mb-2">Pilih Manager</label>
              <div className="flex items-center bg-gray-100 rounded-full px-4 py-2">
                <FiUsers className="text-gray-500 mr-3" />
                <select
                  name="manager_id"
                  value={formData.manager_id}
                  onChange={handleChange}
                  className="w-full bg-transparent outline-none text-gray-700"
                >
                  <option value="">-- Pilih Manager --</option>
                  {managers.map((manager) => (
                    <option key={manager.id} value={manager.id}>
                      {manager.full_name} ({manager.role})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Alamat */}
          <div className="mt-6">
            <label className="block font-medium text-gray-700 mb-2">Alamat</label>
            <div className="flex items-start bg-gray-100 rounded-2xl px-4 py-3">
              <FiMapPin className="text-gray-500 mr-3 mt-1" />
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="3"
                className="w-full bg-transparent outline-none resize-none text-gray-700"
              ></textarea>
            </div>
          </div>

          {/* Tombol Aksi */}
          <div className="flex justify-end mt-8 space-x-4">
            <button
              type="button"
              onClick={() => navigate("/enterprise/outlet")}
              className="px-6 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-full transition font-medium"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition font-medium"
            >
              Simpan
            </button>
          </div>
        </form>

        {showSuccessModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-96 text-center animate-bounce-in">
              <h2 className="text-2xl font-bold text-green-600 mb-2">Berhasil!</h2>
              <p className="text-gray-700">Data outlet berhasil diperbarui.</p>
            </div>
          </div>
        )}

        {showAccessDenied && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-lg w-96 text-center">
              <h2 className="text-xl font-bold text-red-600 mb-4">Akses Ditolak</h2>
              <p className="text-gray-600 mb-4">
                Anda tidak memiliki hak akses untuk memasuki halaman ini.
              </p>
              <button
                onClick={() => navigate("/dashboard")}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-2 rounded-full transition"
              >
                Kembali ke Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditOutletForm;
